import { Box, Stack } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import {
  VerticalTabProps,
  VerticalTabs,
} from '@/components/navigation/VerticalTabs/VerticalTabs';

import { DocumentsProcessingCallout } from '../../components/DocumentsProcessingCallout';
import { StepIntro } from '../../components/StepIntro';
import { useManageIntegrationDataContext } from '../../context/manageIntegrationData.context';
import { useIntegrationProviderName } from '../../hooks/useIntegrationProviderName';
import { useShowEmoneyImportPanes } from '../../hooks/useShowEmoneyImportPanes';
import { TAB_RAIL_WIDTH } from '../../ManageIntegrationDataModal.constants';
import {
  ImportDataPane,
  ManageIntegrationDataPane,
} from '../../ManageIntegrationDataModal.types';
import { CashflowsImportPane } from './CashflowsImportPane';
import { ImportWarningCallout } from './components/ImportWarningCallout';
import { EntitiesImportPane } from './EntitiesImportPane';
import { LiabilitiesImportPane } from './LiabilitiesImportPane';
import { LifeInsuranceImportPane } from './LifeInsuranceImportPane';

export interface ImportDataStepProps {
  activePane: ImportDataPane;
}

const EMONEY_IMPORT_ITEM_NAMES: Record<ImportDataPane, string> = {
  [ManageIntegrationDataPane.ImportDataEntities]: 'entities',
  [ManageIntegrationDataPane.ImportDataLiabilities]: 'liabilities',
  [ManageIntegrationDataPane.ImportDataLifeInsurance]: 'life insurance',
  [ManageIntegrationDataPane.ImportDataCashflows]: 'cash flows',
};

/**
 * Step 4: create new Luminary entities from integration data that isn't linked
 * to anything yet.
 *
 * Addepar households have only entities to import, so the step renders as a
 * single table. eMoney additionally exposes liabilities and life insurance,
 * which appear as sibling tabs.
 */
export function ImportDataStep({ activePane }: ImportDataStepProps) {
  const {
    goToPane,
    entitiesImportForm,
    liabilitiesImportForm,
    lifeInsuranceImportForm,
    cashflowsImportForm,
  } = useManageIntegrationDataContext();
  const integrationProviderName = useIntegrationProviderName();
  const showEmoneyPanes = useShowEmoneyImportPanes();
  const [hasAvailableEntities, setHasAvailableEntities] = useState(true);
  const isEmptyEntitiesPane =
    activePane === ManageIntegrationDataPane.ImportDataEntities &&
    !hasAvailableEntities;

  const tabs = useMemo<VerticalTabProps<ImportDataPane>[]>(
    () => [
      {
        value: ManageIntegrationDataPane.ImportDataEntities,
        display: 'Entities',
      },
      {
        value: ManageIntegrationDataPane.ImportDataLiabilities,
        display: 'Liabilities',
      },
      {
        value: ManageIntegrationDataPane.ImportDataLifeInsurance,
        display: 'Life insurance',
      },
      {
        value: ManageIntegrationDataPane.ImportDataCashflows,
        display: 'Cash flows',
      },
    ],
    []
  );

  // A deep link can land on a liabilities or life-insurance tab that this
  // household doesn't have; fall back to the one tab that always exists.
  useEffect(() => {
    if (showEmoneyPanes) return;
    if (activePane !== ManageIntegrationDataPane.ImportDataEntities) {
      goToPane(ManageIntegrationDataPane.ImportDataEntities);
    }
  }, [activePane, goToPane, showEmoneyPanes]);

  return (
    <Stack height="100%">
      {!isEmptyEntitiesPane && (
        <StepIntro
          title="Import as new entities"
          subtitle={`Select data from ${integrationProviderName} to import into Luminary. Nested items that are selected will be imported as separate entities.`}
        />
      )}
      <DocumentsProcessingCallout />

      <Stack direction="row" spacing={3} alignItems="flex-start" flex={1}>
        {showEmoneyPanes && (
          <Box width={TAB_RAIL_WIDTH} flexShrink={0}>
            <VerticalTabs
              ariaLabel="Import data sections"
              tabs={tabs}
              activeValue={activePane}
              onChange={goToPane}
            />
          </Box>
        )}

        <Box
          flex={1}
          minWidth={0}
          sx={
            isEmptyEntitiesPane
              ? {
                  alignSelf: 'stretch',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }
              : undefined
          }
        >
          {!isEmptyEntitiesPane && (
            <ImportWarningCallout
              message={
                showEmoneyPanes
                  ? `Luminary will create and link ${EMONEY_IMPORT_ITEM_NAMES[activePane]} from eMoney for each selected item. Each item is created from eMoney base facts, and not from hypothetical plan scenarios.`
                  : undefined
              }
            />
          )}

          {activePane === ManageIntegrationDataPane.ImportDataEntities && (
            <EntitiesImportPane
              form={entitiesImportForm}
              onAvailabilityChange={setHasAvailableEntities}
            />
          )}

          {showEmoneyPanes && (
            <>
              {activePane ===
                ManageIntegrationDataPane.ImportDataLiabilities && (
                <LiabilitiesImportPane form={liabilitiesImportForm} />
              )}
              {activePane ===
                ManageIntegrationDataPane.ImportDataLifeInsurance && (
                <LifeInsuranceImportPane form={lifeInsuranceImportForm} />
              )}
              {activePane === ManageIntegrationDataPane.ImportDataCashflows && (
                <CashflowsImportPane form={cashflowsImportForm} />
              )}
            </>
          )}
        </Box>
      </Stack>
    </Stack>
  );
}
